import { useMemo, type ComponentType } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import LocalAirportIcon from '@mui/icons-material/LocalAirport';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import HotelIcon from '@mui/icons-material/Hotel';
import AttractionsIcon from '@mui/icons-material/Attractions';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ForestIcon from '@mui/icons-material/Forest';
import TerrainIcon from '@mui/icons-material/Terrain';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

import type { Location, LocationType } from '@/services/api/types';

/** One icon per location type — the single source of truth other components reuse. */
export const LOCATION_TYPE_ICON: Record<LocationType, ComponentType<SvgIconProps>> = {
  AIRPORT: LocalAirportIcon,
  CITY: LocationCityIcon,
  HOTEL: HotelIcon,
  ATTRACTION: AttractionsIcon,
  BEACH: BeachAccessIcon,
  // A dry forest's tree canopy, a cloud forest's misty, mountainous terrain,
  // and a rain forest's defining rainfall — three distinct silhouettes so
  // none of them read as duplicates of each other.
  TROPICAL_DRY_FOREST: ForestIcon,
  TROPICAL_CLOUD_FOREST: TerrainIcon,
  TROPICAL_RAIN_FOREST: WaterDropIcon,
};

export function LocationTypeIcon({ type, ...props }: { type: LocationType } & SvgIconProps) {
  const Icon = LOCATION_TYPE_ICON[type];
  return <Icon fontSize="small" color="action" {...props} />;
}

interface LocationFieldProps {
  label: string;
  value: Location | null;
  options: Location[];
  loading: boolean;
  onChange: (value: Location | null) => void;
}

const defaultFilter = createFilterOptions<Location>();

/**
 * Pickup/destination picker shared by the public booking search and the
 * admin create-reservation dialog.
 *
 * <p>Nesting is filter-triggered, not always-expanded: with an empty input the
 * list renders flat, exactly as before. Only once typed text matches a city's
 * name (or one of its own children's names) does that city's children appear
 * nested directly beneath it — a city with many hotels does not clutter the
 * list until the customer is actually looking for one of them.
 */
export function LocationField({ label, value, options, loading, onChange }: LocationFieldProps) {
  const bySlug = useMemo(() => new Map(options.map((location) => [location.slug, location])), [options]);

  const childrenByParentSlug = useMemo(() => {
    const map = new Map<string, Location[]>();
    for (const location of options) {
      if (!location.parentSlug) continue;
      const siblings = map.get(location.parentSlug) ?? [];
      siblings.push(location);
      map.set(location.parentSlug, siblings);
    }
    return map;
  }, [options]);

  function filterOptions(candidates: Location[], state: FilterOptionsState<Location>): Location[] {
    if (!state.inputValue.trim()) return candidates;

    const matched = defaultFilter(candidates, state);
    const result: Location[] = [];
    const seen = new Set<string>();

    for (const location of matched) {
      if (seen.has(location.slug)) continue;
      result.push(location);
      seen.add(location.slug);

      if (location.type === 'CITY') {
        for (const child of childrenByParentSlug.get(location.slug) ?? []) {
          if (!seen.has(child.slug)) {
            result.push(child);
            seen.add(child.slug);
          }
        }
      }
    }

    // A matched child whose city did not itself match the typed text still
    // needs its parent shown above it, so the nesting has something to render
    // under.
    for (const location of matched) {
      if (location.parentSlug && !seen.has(location.parentSlug)) {
        const parent = bySlug.get(location.parentSlug);
        if (parent) {
          const firstChildIndex = result.findIndex((candidate) => candidate.parentSlug === parent.slug);
          result.splice(firstChildIndex === -1 ? result.length : firstChildIndex, 0, parent);
          seen.add(parent.slug);
        }
      }
    }

    return result;
  }

  if (loading && options.length === 0) {
    return <Skeleton variant="rounded" height={56} sx={{ flex: 1 }} />;
  }

  return (
    <Autocomplete
      sx={{ flex: 1 }}
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.slug === selected.slug}
      filterOptions={filterOptions}
      loading={loading}
      renderInput={(params) => <TextField {...params} label={label} />}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          {/* A margin on the content, not padding on the <li> itself — MUI's own
              .MuiAutocomplete-option padding-left can outrank an sx override on
              the list item, but nothing outranks pushing the content over. */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', ml: option.parentSlug ? 3 : 0 }}
          >
            <LocationTypeIcon type={option.type} />
            <Box>
              <Box sx={{ fontWeight: 600 }}>{option.name}</Box>
              {option.region && (
                <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{option.region}</Box>
              )}
            </Box>
          </Stack>
        </Box>
      )}
    />
  );
}

export default LocationField;
